import { apiClient } from '../utils/apiClient.js';
import appInsightsLogger from '../utils/appInsights.js';

class OktaService {
  async searchUsers(searchRequest) {
        appInsightsLogger.trackEvent('Okta_User_Search', {
            component: 'oktaService'
        });

        try {
            const result = await apiClient.get('/api/okta/search-users');
            return result;
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'oktaService'
            });

            console.error('Error searching users:', error);
            throw error;
        }
  }

  async registerUser(request) {
    appInsightsLogger.trackEvent('Okta_User_Registration', {
      component: 'oktaService',
      email: request.email
    });

    try {
      const result = await apiClient.post('/api/okta/register-user', request);
      appInsightsLogger.trackEvent('Okta_User_Registration_Success', {
        component: 'oktaService',
        email: request.email
      });
      return result;
    } catch (error) {
      appInsightsLogger.trackException(error, {
        component: 'oktaService',
        action: 'registerUser',
        email: request.email
      });

      console.error('Error registering user with Okta:', error);
      throw error;
    }
  }

  async updateLocalUser(request) {
    appInsightsLogger.trackEvent('Okta_User_Update_Local', {
      component: 'oktaService',
      userId: request.userId,
      email: request.email
    });

    try {
      const result = await apiClient.post('/api/okta/update-local-user', request);
      appInsightsLogger.trackEvent('Okta_User_Update_Local_Success', {
        component: 'oktaService',
        userId: request.userId,
        email: request.email
      });
      return result;
    } catch (error) {
      appInsightsLogger.trackException(error, {
        component: 'oktaService',
        action: 'updateLocalUser',
        userId: request.userId,
        email: request.email
      });

      console.error('Error updating user in local database:', error);
      throw error;
    }
  }

  async enableUser(oktaUserId, currentStatus) {
        try {
            await apiClient.post('/api/okta/enable-user', {
                oktaUserId: oktaUserId,
                currentStatus: currentStatus || 'UNKNOWN'
            });
            return true;
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'oktaService',
                oktaUserId: oktaUserId,
                currentStatus: currentStatus
            });

            console.error('Error enabling user:', error);
            throw error;
        }
    }

    async disableUser(oktaUserId) {
        try {
            await apiClient.post('/api/okta/disable-user', { oktaUserId });
            return true;
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'oktaService',
                oktaUserId: oktaUserId
            });

            console.error('Error disabling user:', error);
            throw error;
        }
    }

    async suspendUser(oktaUserId) {
        try {
            await apiClient.post('/api/okta/suspend-user', { oktaUserId });
            return true;
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'oktaService',
                oktaUserId: oktaUserId
            });

            console.error('Error suspending user:', error);
            throw error;
        }
    }

    async unlockUser(oktaUserId) {
        try {
            await apiClient.post('/api/okta/unlock-user', { oktaUserId });
            return true;
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'oktaService',
                oktaUserId: oktaUserId
            });

            console.error('Error unlocking user:', error);
            throw error;
        }
    }

    async resetPassword(oktaUserId) {
        try {
            // Validate oktaUserId
            if (!oktaUserId || (typeof oktaUserId === 'string' && oktaUserId.trim().length === 0)) {
                throw new Error('Okta User ID is required');
            }

            appInsightsLogger.trackEvent('Okta_Reset_Password', {
                component: 'oktaService',
                oktaUserId: oktaUserId
            });

            // Send just the oktaUserId string to the backend
            const result = await apiClient.post('/api/okta/reset-password', oktaUserId);

            appInsightsLogger.trackEvent('Okta_Reset_Password_Success', {
                component: 'oktaService',
                oktaUserId: oktaUserId
            });

            return result;
        } catch (error) {
            appInsightsLogger.trackException(error, {
                component: 'oktaService',
                action: 'resetPassword',
                oktaUserId: oktaUserId
            });

            console.error('Error resetting password:', error);
            throw error;
        }
    }
}

export const oktaService = new OktaService();